import { useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import isEmpty from "lodash/isEmpty";
import { usePostHog } from "posthog-js/react";
import { useReducer, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import usePublicApi from "@/core/api/usePublicApi";
import { userKey } from "@/user/query-keys";
import { DetailedUser } from "@/user/user";
import { getEnrichedUser, isApOnly } from "@/user/utils";
import {
  IUser,
  LoginCredentials,
  AuthState,
  AuthRequestOptions,
  ForgotPasswordCredentials,
  ResetPasswordCodeCredentials,
  ResetPasswordCredentials,
} from "./auth";
import AuthContext from "./auth-context";
import {
  getPasswordResetRequestError,
  getPasswordResetSubmissionError,
  getPasswordResetVerificationError,
  isPasswordResetThrottledError,
  isThrottledApiError,
  PasswordResetError,
  PASSWORD_RESET_ERROR_CODE,
  RESEND_CODE_COOLDOWN_SECONDS,
} from "./password-reset-error";
import {
  normalizeResetEmail,
  PASSWORD_RESET_SOURCE_PARAM,
  WELCOME_EMAIL_PASSWORD_RESET_QUERY,
  WELCOME_EMAIL_PASSWORD_RESET_SOURCE,
} from "./password-reset-flow";
import { dismissThrottledResetToast } from "./throttled-reset-toast";
import { getTokenRole } from "./token-role";

type AuthAction =
  | { type: "SET_LOADING" }
  | { type: "SET_USER"; payload: { user: IUser; token: string } }
  | { type: "LOGOUT" }
  | { type: "SET_FORGOT_PASSWORD_EMAIL"; payload: string }
  | { type: "SET_FORGOT_PASSWORD_VERIFICATION_CODE"; payload: string }
  | { type: "CLEAR_PASSWORD_RESET" }
  | {
      type: "SET_RESET_CODE_RESEND_AVAILABLE_AT";
      payload: { email: string; availableAt: Dayjs };
    }
  | {
      type: "SET_IMPERSONATION";
      payload: { user: IUser };
    }
  | { type: "CLEAR_IMPERSONATION" };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: true };
    case "SET_USER":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case "LOGOUT":
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        forgotPasswordEmail: null,
        forgotPasswordVerificationCode: null,
        resetCodeResendAvailableAt: {},
        isImpersonating: false,
        impersonatedUser: null,
      };
    case "SET_FORGOT_PASSWORD_EMAIL":
      return { ...state, forgotPasswordEmail: action.payload };
    case "SET_FORGOT_PASSWORD_VERIFICATION_CODE":
      return { ...state, forgotPasswordVerificationCode: action.payload };
    case "CLEAR_PASSWORD_RESET":
      return {
        ...state,
        forgotPasswordEmail: null,
        forgotPasswordVerificationCode: null,
        resetCodeResendAvailableAt: {},
      };
    case "SET_RESET_CODE_RESEND_AVAILABLE_AT":
      return {
        ...state,
        resetCodeResendAvailableAt: {
          ...state.resetCodeResendAvailableAt,
          [normalizeResetEmail(action.payload.email)]:
            action.payload.availableAt,
        },
      };
    case "SET_IMPERSONATION":
      return {
        ...state,
        isImpersonating: true,
        impersonatedUser: action.payload.user,
      };
    case "CLEAR_IMPERSONATION":
      return {
        ...state,
        isImpersonating: false,
        impersonatedUser: null,
      };
    default:
      return state;
  }
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const storedToken = localStorage.getItem("token");
  const storedUser = localStorage.getItem("user");
  const posthog = usePostHog();
  const refreshPromise = useRef<Promise<string | null> | null>(null);
  const publicApi = usePublicApi();
  const queryClient = useQueryClient();

  const [state, dispatch] = useReducer(authReducer, {
    user: storedUser ? JSON.parse(storedUser) : null,
    token: storedToken,
    isAuthenticated: !!storedToken,
    isLoading: false,
    forgotPasswordEmail: null,
    forgotPasswordVerificationCode: null,
    resetCodeResendAvailableAt: {},
    isImpersonating: false,
    impersonatedUser: null,
  });

  const getResetFlowQueryString = useCallback(() => {
    const source = new URLSearchParams(location.search).get(
      PASSWORD_RESET_SOURCE_PARAM,
    );
    if (source !== WELCOME_EMAIL_PASSWORD_RESET_SOURCE) {
      return "";
    }

    return WELCOME_EMAIL_PASSWORD_RESET_QUERY;
  }, [location.search]);

  const getResetCodeResendAvailableAt = useCallback(
    (email: string | null) =>
      email
        ? (state.resetCodeResendAvailableAt[normalizeResetEmail(email)] ?? null)
        : null,
    [state.resetCodeResendAvailableAt],
  );

  // Opens code entry for an email, whether a code was just sent or the user
  // already has one and a new send was refused.
  const openResetCodeEntry = useCallback(
    (email: string) => {
      dismissThrottledResetToast();
      dispatch({ type: "SET_FORGOT_PASSWORD_EMAIL", payload: email });
      const resetFlowQuery = getResetFlowQueryString();
      navigate(
        resetFlowQuery
          ? `/reset-password-code?${resetFlowQuery}`
          : "/reset-password-code",
      );
    },
    [navigate, getResetFlowQueryString],
  );

  const sendPasswordResetCode = useCallback(
    async (email: string, options?: AuthRequestOptions) => {
      const armResendCooldown = (seconds: number) => {
        dispatch({
          type: "SET_RESET_CODE_RESEND_AVAILABLE_AT",
          payload: { email, availableAt: dayjs().add(seconds, "second") },
        });
      };

      try {
        await publicApi.post(
          "/password_reset",
          { email },
          { signal: options?.signal },
        );
        armResendCooldown(RESEND_CODE_COOLDOWN_SECONDS);
        return null;
      } catch (error) {
        if (!isThrottledApiError(error)) {
          return getPasswordResetRequestError(error);
        }

        const seconds = error.retryAfterSeconds ?? RESEND_CODE_COOLDOWN_SECONDS;
        armResendCooldown(seconds);
        return getPasswordResetRequestError(error, seconds);
      }
    },
    [publicApi],
  );

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      dispatch({ type: "SET_LOADING" });
      try {
        posthog?.capture("login_attempted", { email: credentials.email });

        const response = await publicApi.post("/login", {
          user: { ...credentials },
        });

        const userData = response.data?.data;
        const token = response.headers.authorization?.replace("Bearer ", "");

        if (userData?.needs_password_reset) {
          posthog?.capture("password_reset_required", {
            email: credentials.email,
          });

          const resetError = await sendPasswordResetCode(credentials.email);
          if (resetError && !isPasswordResetThrottledError(resetError)) {
            throw resetError;
          }

          openResetCodeEntry(credentials.email);
          return;
        }

        if (!token || isEmpty(userData)) throw new Error("No token received");

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));

        // Identify user in PostHog with all available fields
        posthog?.identify(userData.id, {
          email: userData.email,
          distinct_id: userData.id,
          first_name: userData.first_name,
          last_name: userData.last_name,
          role: userData.role,
          parentable_id: userData.parentable_id,
          parentable_type: userData.parentable_type,
        });

        // Set parent organization as a group
        if (userData.parentable_type && userData.parentable_id) {
          posthog?.group(
            userData.parentable_type.toLowerCase(),
            userData.parentable_id,
          );
        }

        posthog?.capture("login_succeeded", {
          email: userData.email,
          role: userData.role,
          parentable_type: userData.parentable_type,
        });

        dispatch({ type: "SET_USER", payload: { user: userData, token } });

        // Get the intended path from location state or default to "/"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const from = (location.state as any)?.from?.pathname || "/";
        navigate(isApOnly(userData.role) ? "/" : from, { replace: true });
      } catch (error) {
        posthog?.capture("login_failed", { email: credentials.email });
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        dispatch({ type: "LOGOUT" });
        throw error;
      }
    },
    [
      publicApi,
      navigate,
      location,
      posthog,
      openResetCodeEntry,
      sendPasswordResetCode,
    ],
  );

  const logout = useCallback(async () => {
    try {
      // End impersonation session if currently impersonating
      if (state.isImpersonating && state.token) {
        try {
          await publicApi.delete("/admin_impersonation", {
            headers: {
              Authorization: `Bearer ${state.token}`,
            },
          });
        } catch (error) {
          // Don't throw - we still want to log out even if this fails
          console.error("Failed to end impersonation during logout:", error);
        }
      }

      // Regular logout
      if (state.token) {
        await publicApi.delete("/logout", {
          headers: {
            Authorization: `Bearer ${state.token}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      posthog?.capture("logout");
      queryClient.clear();
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      dispatch({ type: "LOGOUT" });
      navigate("/login");
    }
  }, [publicApi, posthog, state.token, state.isImpersonating, navigate]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      if (refreshPromise.current) {
        return refreshPromise.current;
      }

      refreshPromise.current = (async () => {
        const { headers } = await publicApi.post("/token_refresh", {});

        const newToken = headers.authorization?.replace("Bearer ", "");
        if (!newToken) {
          throw new Error("No token received from refresh");
        }

        if (!state.user) throw new Error("No user available for refresh");
        const user = { ...state.user, role: getTokenRole(newToken) };

        localStorage.setItem("token", newToken);
        localStorage.setItem("user", JSON.stringify(user));
        queryClient.setQueryData<DetailedUser>(userKey(user.id), (current) =>
          current ? getEnrichedUser({ ...current, role: user.role }) : current,
        );
        dispatch({ type: "SET_USER", payload: { user, token: newToken } });
        return newToken;
      })();

      const result = await refreshPromise.current;
      refreshPromise.current = null;
      return result;
    } catch (error) {
      console.error("Token refresh failed:", error);
      refreshPromise.current = null;
      await logout();
      throw error;
    }
  }, [publicApi, logout, state.user, queryClient]);

  const forgotPassword = useCallback(
    async (
      credentials: ForgotPasswordCredentials,
      options?: AuthRequestOptions,
    ) => {
      try {
        posthog?.capture("forgot_password_requested", {
          email: credentials.email,
        });
        const resetError = await sendPasswordResetCode(
          credentials.email,
          options,
        );
        if (resetError) throw resetError;
        posthog?.capture("forgot_password_email_sent", {
          email: credentials.email,
        });
        openResetCodeEntry(credentials.email);
      } catch (error) {
        posthog?.capture("forgot_password_failed", {
          email: credentials.email,
        });
        console.error(error);
        throw error;
      }
    },
    [posthog, openResetCodeEntry, sendPasswordResetCode],
  );

  const verifyResetPasswordCode = useCallback(
    async (
      credentials: ResetPasswordCodeCredentials,
      options?: AuthRequestOptions,
    ) => {
      try {
        await publicApi.post("/password_reset/verify", credentials, {
          signal: options?.signal,
        });
      } catch (error) {
        console.error(error);
        throw getPasswordResetVerificationError(error);
      }
    },
    [publicApi],
  );

  const resetPasswordCode = useCallback(
    async (credentials: ResetPasswordCodeCredentials) => {
      await verifyResetPasswordCode(credentials);
      dispatch({
        type: "SET_FORGOT_PASSWORD_EMAIL",
        payload: credentials.email,
      });
      dispatch({
        type: "SET_FORGOT_PASSWORD_VERIFICATION_CODE",
        payload: credentials.verification_code,
      });
      const resetFlowQuery = getResetFlowQueryString();
      navigate(
        resetFlowQuery
          ? `/reset-password?${resetFlowQuery}`
          : "/reset-password",
      );
    },
    [verifyResetPasswordCode, navigate, getResetFlowQueryString],
  );

  const resetPassword = useCallback(
    async (credentials: ResetPasswordCredentials) => {
      try {
        await publicApi.post("/password_reset/submit", credentials);
      } catch (error) {
        console.error("Password reset submission failed:", error);
        throw getPasswordResetSubmissionError(error);
      }

      dispatch({ type: "CLEAR_PASSWORD_RESET" });

      try {
        await login({
          email: credentials.email,
          password: credentials.password,
        });
      } catch (error) {
        console.error("Automatic login after password reset failed:", error);
        throw new PasswordResetError(
          PASSWORD_RESET_ERROR_CODE.loginFailed,
          "Your password was changed, but we couldn't sign you in automatically. Please log in with your new password.",
        );
      }
    },
    [publicApi, login],
  );

  const handleImpersonationChange = useCallback(
    (impState: {
      isImpersonating: boolean;
      impersonatedUser: IUser | null;
    }) => {
      if (impState.isImpersonating) {
        dispatch({
          type: "SET_IMPERSONATION",
          payload: { user: impState.impersonatedUser! },
        });
      } else {
        dispatch({ type: "CLEAR_IMPERSONATION" });
      }
    },
    [],
  );

  const contextValue = {
    ...state,
    user: state.isImpersonating ? state.impersonatedUser : state.user,
    login,
    logout,
    refreshToken,
    forgotPassword,
    openResetCodeEntry,
    getResetCodeResendAvailableAt,
    resetPasswordCode,
    verifyResetPasswordCode,
    resetPassword,
    handleImpersonationChange,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
